import type { AlertEvent, AllocationAsset, AllocationRecord, DailyBar, ExecutionRecord, Instrument, LatestQuote, MonitoringMetric, RulePlanBundle } from "../types";
import { buildLadder, monitoringMetrics } from "../utils/calculations";

const legacyInstruments = [
  { id: "spx", name: "S&P 500", ticker: "SPX", country: "United States", region: "US", kind: "spot-index", currency: "USD", timezone: "America/New_York", primarySource: "Yahoo", fallbackSources: ["Investing.com"] },
  { id: "nasdaq", name: "NASDAQ Composite", ticker: "IXIC", country: "United States", region: "US", kind: "spot-index", currency: "USD", timezone: "America/New_York", primarySource: "Yahoo", fallbackSources: ["Investing.com"] },
  { id: "nasdaq100", name: "Nasdaq 100", ticker: "NDX", country: "United States", region: "US", kind: "spot-index", currency: "USD", timezone: "America/New_York", primarySource: "Yahoo", fallbackSources: ["Investing.com"] },
  { id: "dowjones", name: "Dow Jones Industrial Average", ticker: "DJI", country: "United States", region: "US", kind: "spot-index", currency: "USD", timezone: "America/New_York", primarySource: "Yahoo", fallbackSources: ["Investing.com"] },
  { id: "kospi", name: "KOSPI", ticker: "KOSPI", country: "Korea", region: "Asia", kind: "spot-index", currency: "KRW", timezone: "Asia/Seoul", primarySource: "Yahoo", fallbackSources: ["Naver"] },
  { id: "kosdaq", name: "KOSDAQ", ticker: "KOSDAQ", country: "Korea", region: "Asia", kind: "spot-index", currency: "KRW", timezone: "Asia/Seoul", primarySource: "Yahoo", fallbackSources: ["Naver"] },
  { id: "kospi200", name: "KOSPI 200", ticker: "KOSPI2", country: "Korea", region: "Asia", kind: "spot-index", currency: "KRW", timezone: "Asia/Seoul", primarySource: "Yahoo", fallbackSources: ["Naver"] },
  { id: "spx-fut", name: "S&P 500 Futures", ticker: "ES", country: "United States", region: "US", kind: "futures-index", currency: "USD", timezone: "America/New_York", primarySource: "Yahoo", fallbackSources: ["Investing.com"] },
  { id: "nasdaq-fut", name: "Nasdaq 100 Futures", ticker: "NQ", country: "United States", region: "US", kind: "futures-index", currency: "USD", timezone: "America/New_York", primarySource: "Yahoo", fallbackSources: ["Investing.com"] },
  { id: "dow-fut", name: "Dow Jones Futures", ticker: "YM", country: "United States", region: "US", kind: "futures-index", currency: "USD", timezone: "America/New_York", primarySource: "Yahoo", fallbackSources: ["Investing.com"] }
] as const;

export const instruments: Instrument[] = legacyInstruments.map((item, index) => ({
  ...item,
  fallbackSources: [...item.fallbackSources],
  assetClass: item.kind === "futures-index" ? "futures" : "index",
  exchange: item.country === "Korea" ? "KRX" : "US",
  unit: "index points",
  planEligible: true,
  monitoringEnabled: true,
  displayOrder: index + 1,
  catalogStatus: "active"
}));

export const fxPairs: string[] = [];

const basePrices = [
  5120, 23500, 30000, 47000, 3200, 850, 430, 5158, 30050, 43200
];

export function makeHistory(seed: number, current: number): DailyBar[] {
  const rawBars: DailyBar[] = [];
  const start = new Date("2016-07-01T00:00:00Z");
  const end = new Date("2026-07-08T00:00:00Z");
  let previousClose = current * (0.46 + (seed % 7) * 0.012);
  let tradingIndex = 0;

  for (let date = new Date(start); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    const day = date.getUTCDay();
    if (day === 0 || day === 6) continue;

    const progress = tradingIndex / 2520;
    const trend = 0.54 + progress * 0.8;
    const cycle = Math.sin((tradingIndex + seed * 11) / 48) * 0.055 + Math.sin((tradingIndex + seed) / 137) * 0.095;
    const pandemicShock = -0.24 * Math.exp(-Math.pow((progress - 0.37) / 0.018, 2));
    const tighteningShock = -0.14 * Math.exp(-Math.pow((progress - 0.74) / 0.035, 2));
    const dailyTexture = Math.sin((tradingIndex + seed * 17) / 5) * 0.012 + Math.cos((tradingIndex + seed * 31) / 11) * 0.006;
    const close = current * (trend + cycle + pandemicShock + tighteningShock) * (0.95 + (seed % 7) * 0.006) * (1 + dailyTexture);
    const normalizedClose = Math.max(close, current * 0.3);
    const open = previousClose * (1 + Math.sin((tradingIndex + seed) / 19) * 0.0025);
    const high = Math.max(open, normalizedClose) * (1.004 + Math.abs(Math.sin((tradingIndex + seed) / 23)) * 0.006);
    const low = Math.min(open, normalizedClose) * (0.996 - Math.abs(Math.cos((tradingIndex + seed) / 29)) * 0.006);
    rawBars.push({
      date: date.toISOString().slice(0, 10),
      open,
      high,
      low,
      close: normalizedClose
    });
    previousClose = normalizedClose;
    tradingIndex += 1;
  }

  const lastRawClose = rawBars.at(-1)?.close ?? current;
  const scale = lastRawClose > 0 ? (current * 0.997) / lastRawClose : 1;
  const bars = rawBars.map((bar) => ({
    date: bar.date,
    open: (bar.open ?? bar.close) * scale,
    high: (bar.high ?? bar.close) * scale,
    low: (bar.low ?? bar.close) * scale,
    close: bar.close * scale
  }));

  bars.push({ date: "2026-07-09", open: current * 0.997, high: current * 1.006, low: current * 0.991, close: current });
  return bars;
}

export const monitoringMetricsMock: MonitoringMetric[] = instruments.map((instrument, index) => {
  const price = basePrices[index];
  const history = makeHistory(index + 1, price);
  const metrics = monitoringMetrics(history, price);
  const stale = index % 9 === 0;
  const quote: LatestQuote = {
    instrumentId: instrument.id,
    price,
    changePct: [-1.02, 0.38, -0.74, -1.68, 0.14, -0.42, 0.82, -0.2, 0.31, -0.57][index % 10],
    asOf: stale ? "2026-07-08T20:05:00Z" : "2026-07-09T05:10:00Z",
    freshness: stale ? "stale" : index % 5 === 0 ? "cached" : "delayed",
    source: instrument.primarySource,
    riskLevel: instrument.primarySource === "Yahoo" ? "high" : instrument.primarySource === "Investing.com" ? "medium" : "low",
    staleReason: stale ? "Provider refresh pending; cached close is displayed." : undefined
  };
  return {
    instrument,
    quote,
    window: "10Y",
    ...metrics,
    coverage: {
      requestedStartDate: history[0]?.date ?? "",
      startDate: history[0]?.date ?? "",
      endDate: history.at(-1)?.date ?? "",
      points: history.length,
      coveragePct: 100
    },
    qualityFlags: [],
    licenseStatus: "interim",
    history
  };
});

export const ladderBudgets = [10000, 15000, 20000, 20000, 20000, 15000];

export const allocationAssets: AllocationAsset[] = [
  { ticker: "SPY", name: "US Equity", weightPct: 30, color: "#2267b8" },
  { ticker: "QQQ", name: "US Growth", weightPct: 20, color: "#6d3aa6" },
  { ticker: "KODEX 200", name: "KR Equity", weightPct: 20, color: "#168760" },
  { ticker: "Cash", name: "USD Reserve", weightPct: 10, color: "#a87818" },
  { ticker: "Others", name: "Tactical", weightPct: 20, color: "#596a7c" }
];

export const executionRecords: ExecutionRecord[] = [
  { id: "execution-cp-01-spx-trigger", planId: "cp-01-spx", stepId: "cp-01-spx-trigger", label: "Trigger / Step 1", plannedLevel: 5082.5, actualLevel: 5079.1, plannedAmount: 10000, executedAmount: 10000, executedAt: "2026-06-18 10:42 ET", status: "executed" },
  { id: "execution-cp-01-spx-step-1", planId: "cp-01-spx", stepId: "cp-01-spx-step-1", label: "Step 2", plannedLevel: 4975.5, actualLevel: 4971.8, plannedAmount: 15000, executedAmount: 12000, executedAt: "2026-06-19 11:08 ET", status: "executed" },
  { id: "execution-cp-01-spx-step-2", planId: "cp-01-spx", stepId: "cp-01-spx-step-2", label: "Step 3", plannedLevel: 4868.5, plannedAmount: 20000, executedAmount: 0, status: "pending" },
  { id: "execution-cp-01-spx-step-3", planId: "cp-01-spx", stepId: "cp-01-spx-step-3", label: "Step 4", plannedLevel: 4761.5, plannedAmount: 20000, executedAmount: 0, status: "pending" },
  { id: "execution-cp-01-spx-step-4", planId: "cp-01-spx", stepId: "cp-01-spx-step-4", label: "Step 5", plannedLevel: 4654.5, plannedAmount: 20000, executedAmount: 0, status: "pending" },
  { id: "execution-cp-01-spx-step-5", planId: "cp-01-spx", stepId: "cp-01-spx-step-5", label: "Step 6", plannedLevel: 4547.5, plannedAmount: 15000, executedAmount: 0, status: "pending" }
];

export function makeDefaultPlan(): RulePlanBundle {
  const planId = "cp-01-spx";
  const rawSteps = buildLadder({
    referenceHigh: 5350,
    triggerDropPct: 5,
    ladderCount: 5,
    spacingPctPoint: 2,
    spacingBasis: "prior-high",
    direction: "CP",
    budgets: ladderBudgets
  });
  const steps = rawSteps.map((step) => ({ ...step, id: `${planId}-${step.id}` }));
  const allocations: AllocationRecord[] = steps.flatMap((step) =>
    allocationAssets.map((asset) => ({
      id: `${planId}-${step.id}-${asset.ticker}`.replace(/[^a-zA-Z0-9_-]/g, "-"),
      planId,
      stepId: step.id,
      assetTicker: asset.ticker,
      assetName: asset.name,
      weightPct: asset.weightPct,
      plannedAmount: (step.budget * asset.weightPct) / 100
    }))
  );

  return {
    id: planId,
    name: "CP 01 - SPX",
    direction: "CP",
    instrumentId: "spx",
    referenceMode: "intraday-high",
    referenceHigh: 5350,
    triggerPct: 5,
    ladderCount: 5,
    spacingPctPoint: 2,
    spacingBasis: "prior-high",
    budgetMode: "amount",
    budgetCurrency: "USD",
    totalBudget: 100000,
    status: "active",
    updatedAt: "2026-07-09T05:10:00Z",
    steps,
    allocations
  };
}

export const planBundlesMock: RulePlanBundle[] = [makeDefaultPlan()];

export const alertEventsMock: AlertEvent[] = [
  {
    id: "alert-seeded-trigger",
    planId: "cp-01-spx",
    stepId: "cp-01-spx-trigger",
    severity: "high",
    dedupeKey: "cp-01-spx:trigger",
    status: "queued",
    telegramPreview: "[CP Dashboard] Trigger armed for CP 01 - SPX at 5,082.50.",
    createdAt: "2026-07-09T05:10:00Z"
  }
];
